import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUploader } from '@/hooks/useUploader';
import ProductForm from '@/components/ProductForm';
import AIAssistantPanel from '@/components/AIAssistantPanel';
import { api } from '@/lib/api';

export default function EditProduct() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { formData, updateField, errors, loading: saving, submit } = useUploader();

  useEffect(() => {
    if (!id) return;

    const fetchProduct = async () => {
      try {
        const product = await api.products.get(id);
        for (const [key, value] of Object.entries(product)) {
          if (value !== undefined) {
            updateField(key as keyof typeof product, value);
          }
        }
      } catch {
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleSubmit = async () => {
    const savedId = await submit();
    if (savedId) {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <p className="text-center text-destructive">{error}</p>
        <button onClick={() => navigate('/')} className="btn-primary mt-4">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Edit Product</h2>
          <p className="text-muted-foreground">Update product details</p>
        </div>
        <button onClick={() => navigate('/')} className="btn-secondary">
          Back to Dashboard
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ProductForm
            data={formData}
            errors={errors}
            onChange={updateField}
            onSubmit={handleSubmit}
            loading={saving}
            mode="edit"
          />
        </div>
        <div>
          <AIAssistantPanel
            productName={formData.name}
            category={formData.category}
            cost={formData.cost}
            onAcceptDescription={(desc) => updateField('description', desc)}
            onAcceptSpecs={(specs) => updateField('specs', specs)}
            onAcceptPrice={(price) => updateField('price', price)}
          />
        </div>
      </div>
    </div>
  );
}
